import React, { FC, useState, useRef, useEffect } from 'react'
import { useSwipeable } from 'react-swipeable'

// MUI
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import useMediaQuery from '@mui/material/useMediaQuery'
import Bullets from './Bullets'

import IconButton from '@mui/material/IconButton'
import SvgIcon from '@mui/material/SvgIcon'
// Icons
import Expand_left from '@tdcerhverv/parrotfish/dist/icons/Icons/Expand_left.svg'
import Expand_right from '@tdcerhverv/parrotfish/dist/icons/Icons/Expand_right.svg'
import CardComponent from '../../../Card'
import CodeModule from '../../../CodeModule'
import CallToAction from '../../../CallToAction'
import HelpImage from '../../../Image/Variants/HelpImage'
import { ContentfulToComponent } from '../../../RichText/types'

export interface ISlideState {
  slidePosition: number
  activeSlide: number
}
export type CarouselLayoutComponent = Queries.CAROUSELFragment
const CarouselLayout: FC<CarouselLayoutComponent> = ({ carouselContent, issiderbarCarousal }) => {
  const carousalComponentMap: Partial<ContentfulToComponent> = {
    'HelpUniverseComponentImage': HelpImage,
    'HelpUniverseComponentCard': CardComponent,
    'HelpUniverseModuleDynamicComponents': CodeModule,
    'HelpUniverseComponentCta': CallToAction,
  } as const
  const theme = useTheme()
  const groupedContent = carouselContent ? Array.from(carouselContent) : []
  const length = groupedContent.length
  const slideGap = 20
  let slideLength = length > 3 ? 3 : length

  const isMdDevice = useMediaQuery(useTheme().breakpoints.between('md', 'lg'))
  const isSmDevice = useMediaQuery(useTheme().breakpoints.down('md'))

  if (isMdDevice) {
    slideLength = length >= 2 ? 2 : 1
  } else if (isSmDevice) {
    slideLength = 1
  }
  if (issiderbarCarousal) slideLength = 1

  const getAllSlideLength = () => {
    return slideElementWidth * (length - slideLength)
  }

  const [slideElementWidth, setSlideElementWidth] = useState<number>(0)
  const [slideState, setSlideState] = useState<ISlideState>({
    slidePosition: 0,
    activeSlide: 0,
  })
  const [windowSize, setWindowSize] = useState<number>(0)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const timerId = setInterval(nextSlide, 7000)
    return () => clearInterval(timerId)
  }, [slideElementWidth, slideState.slidePosition])

  const styles = {
    root: {
      overflow: 'hidden',
      position: 'relative',
      width: `100%`,
    },
    container: {
      padding: 0,
      width: `100%`,
      overflow: 'hidden',
    },

    sliderContainer: {
      display: 'grid',
      gridGap: `${slideGap}px`,
      gridTemplateRows: 'none',
      gridAutoFlow: 'column',
      gridAutoColumns: `calc((100% - (${slideLength} - 1) * ${slideGap}px) / ${slideLength})`,
      margin: 0,
      padding: 0,
      marginBottom: '15px',
      minHeight: '200px',
      transition: `transform 300ms ease`,
      transform: `translateX(-${slideState.slidePosition}px)`,
    },
    controlsContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      '& > *': {},
      [theme.breakpoints.up('sm')]: {
        '& > *:not(a)': {
          zIndex: 100,
          flex: '1 0 50%',
        },
      },
    },

    carouselArrow: {
      position: 'absolute',
      top: '40%',
      height: '40px',
      width: '40px',
      backgroundColor: theme.palette.common.white,
      color: theme.palette.common.black,
      borderRadius: '50%',
      justifyContent: 'center',
      opacity: '50%',
      alignItems: 'center',
      boxShadow: '0px 5px 8px 0px rgba(26, 26, 26, 0.14), 0px 1px 14px 0px rgba(26, 26, 26, 0.12)',
      zIndex: '10',
      '&:hover': {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.common.white,
      },

      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    },
    prevArrow: {
      left: '5px',
    },
    nextArrow: {
      right: '5px',
    },
  }

  useEffect(() => {
    const handleResize = () => {
      setWindowSize(window.innerWidth)
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    setSlideState({
      slidePosition: 0,
      activeSlide: 0,
    })
    setSlideElementWidth(
      ref.current?.children[0].getBoundingClientRect().width ?? 0, // Getting the exact width of the slide element widthin the carousel
    )
  }, [windowSize])

  const nextSlide = () => {
    slideState.slidePosition <= getAllSlideLength()
      ? setSlideState({
          slidePosition: slideState.slidePosition + (slideElementWidth + slideGap),
          activeSlide: slideState.activeSlide + 1,
        })
      : setSlideState({
          slidePosition: 0,
          activeSlide: 0,
        })
  }
  const previousSlide = () => {
    slideState.slidePosition > 0
      ? setSlideState({
          slidePosition: slideState.slidePosition - (slideElementWidth + slideGap),
          activeSlide: slideState.activeSlide - 1,
        })
      : setSlide(slideLength)
  }

  const setSlide = (value: number) => {
    setSlideState({
      slidePosition: (slideElementWidth + slideGap) * value,
      activeSlide: value,
    })
  }
  const swipeHandler = useSwipeable({
    onSwipedLeft: () => nextSlide(),
    onSwipedRight: () => previousSlide(),
    delta: 10,
    preventScrollOnSwipe: true,
    trackMouse: false,
  })

  return (
    <Box aria-roledescription="carousel" sx={styles.root} {...swipeHandler}>
      <IconButton
        id="carousel-previous-slide"
        sx={{ ...styles.prevArrow, ...styles.carouselArrow }}
        onClick={previousSlide}
      >
        <SvgIcon component={Expand_left} viewBox="0 0 48 48" />
      </IconButton>
      <Box sx={styles.sliderContainer} ref={ref}>
        {groupedContent?.map((item, index: number) => {
          const SectionComponent = carousalComponentMap[item?.__typename as keyof typeof carousalComponentMap]
          return <Box key={index}>{SectionComponent && <SectionComponent {...item} />}</Box>
        })}
      </Box>
      <IconButton id="carousel-next-slide" sx={{ ...styles.nextArrow, ...styles.carouselArrow }} onClick={nextSlide}>
        <SvgIcon component={Expand_right} viewBox="0 0 48 48" />
      </IconButton>
      <Box sx={styles.controlsContainer}>
        {length > slideLength && (
          <Box display="flex" flex="1" justifyContent="center">
            <Bullets length={length} slideLength={slideLength} slideState={slideState} setSlide={setSlide} />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default CarouselLayout
